/**
 * Call the original function, but applying the throttle rules.
 *
 * If the throttled function can be run immediately, it is called returns its return value,
 * otherwise, it returns the return value of the last invocation.
 */
export type ThrottledFunction<T extends (...args: unknown[]) => unknown> = (...args: Parameters<T>) => ReturnType<T>;

/**
 * Creates a throttled function that only invokes the provided function `fn` at most once per within a given number of milliseconds `ms`.
 *
 * @param fn a given function to throttle
 * @param ms amount of time in milliseconds to ignore further calls
 * @returns a function wrapper which throttles the `fn` function
 */
export const throttle = <T extends (...args: any[]) => ReturnType<T>>(fn: T, ms: number): ThrottledFunction<T> => {
    let inThrottle: boolean;
    let lastResult: ReturnType<T>;
    return function (this: ThisType<unknown>, ...args: unknown[]): ReturnType<T> {
        if (!inThrottle) {
            inThrottle = true;
            if (ms > 0) setTimeout(() => (inThrottle = false), ms);
            lastResult = fn.apply(this, args);
        }
        return lastResult;
    };
};

/**
 * Creates a throttled function that only invokes the provided function `fn` once.
 *
 * @param fn a given function tor run only once
 * @returns a function wrapper which runs the `fn` function only once
 */
export const once = <T extends (...args: any[]) => ReturnType<T>>(fn: T): ThrottledFunction<T> => throttle(fn, 0);
